import React, { useState } from "react";
import PropTypes from "prop-types";
import Icon from "@mui/material/Icon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Collapse from "@mui/material/Collapse";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { useMaterialUIController } from "context";
import { useNavigate } from "react-router-dom";

function AdListItem({ data, noGutter, setModal, onView }) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [campaignOpen, setCampaignOpen] = useState(false);

  const inDateRange = (data) => {
    const currentDate = new Date();
    const startDate = new Date(data.startDate);
    const endDate = new Date(data.endDate);
    const currentDateOnly = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      currentDate.getDate()
    );
    const normalizedStartDate = new Date(
      startDate.getFullYear(),
      startDate.getMonth(),
      startDate.getDate()
    );
    const normalizedEndDate = new Date(
      endDate.getFullYear(),
      endDate.getMonth(),
      endDate.getDate()
    );
    return currentDateOnly >= normalizedStartDate && currentDateOnly <= normalizedEndDate;
  };

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleDelete = () => {
    handleMenuClose();
    setModal({ ...data, type: "delete" });
  };

  const campaign = data?.campaign;

  return (
    <MDBox
      component="li"
      display="flex"
      justifyContent="space-between"
      alignItems="flex-start"
      bgColor={darkMode ? "transparent" : "grey-100"}
      borderRadius="lg"
      p={2}
      mb={noGutter ? 0 : 1}
      mt={1}
    >
      <MDBox display="flex" flexDirection="column" flex={1}>
        <MDTypography variant="button" fontWeight="medium" textTransform="capitalize">
          {data?.title}
        </MDTypography>

        <MDBox display="flex" gap={2} mt={0.5}>
          <MDTypography variant="caption" color="text">
            <p style={{ fontWeight: "bold", marginTop: "10px" }}>Author note:</p>
            {data?.description}
          </MDTypography>

          <MDBox display="flex" alignItems="center" gap={0.5} sx={{ width: "fit-content" }}>
            <Icon>visibility</Icon>
            <MDTypography variant="caption" fontWeight="medium" color="text">
              {data?.events?.filter((event) => event?.type === "view")?.length || 0}
            </MDTypography>
          </MDBox>

          <MDBox display="flex" alignItems="center" gap={0.5} sx={{ width: "fit-content" }}>
            <Icon>ads_click</Icon>
            <MDTypography variant="caption" fontWeight="medium" color="text">
              {data?.events?.filter((event) => event?.type === "click")?.length || 0}
            </MDTypography>
          </MDBox>
        </MDBox>

        {/* Campaign block */}
        {campaign && (
          <MDBox mt={0.75}>
            <MDBox
              display="flex"
              alignItems="center"
              gap={0.5}
              sx={{ cursor: "pointer", width: "fit-content" }}
              onClick={() => setCampaignOpen((prev) => !prev)}
            >
              <Icon
                sx={{
                  fontSize: "14px !important",
                  color: campaign?.status === "active" ? "green" : "gray",
                }}
              >
                campaign
              </Icon>
              <MDTypography variant="caption" fontWeight="medium" color="text">
                {campaign?.name}
              </MDTypography>
              <MDTypography
                variant="caption"
                fontWeight="medium"
                style={{
                  color: inDateRange(campaign)
                    ? campaign?.status === "active"
                      ? "green"
                      : "red"
                    : "red",
                }}
              >
                ({inDateRange(campaign) ? campaign.status : "Out of Date"})
              </MDTypography>
              <Icon sx={{ fontSize: "14px !important", color: "gray" }}>
                {campaignOpen ? "expand_less" : "expand_more"}
              </Icon>
            </MDBox>

            <Collapse in={campaignOpen}>
              <MDBox
                mt={0.75}
                p={1.25}
                borderRadius="8px"
                sx={{ background: darkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)" }}
                display="flex"
                flexWrap="wrap"
                gap={2}
              >
                {campaign?.description && (
                  <MDBox>
                    <MDTypography
                      variant="caption"
                      color="secondary"
                      fontWeight="bold"
                      display="block"
                    >
                      Description
                    </MDTypography>
                    <MDTypography variant="caption">{campaign?.description}</MDTypography>
                  </MDBox>
                )}
                {campaign?.budget && (
                  <MDBox>
                    <MDTypography
                      variant="caption"
                      color="secondary"
                      fontWeight="bold"
                      display="block"
                    >
                      Budget
                    </MDTypography>
                    <MDTypography variant="caption">${campaign.budget}</MDTypography>
                  </MDBox>
                )}
                {campaign?.regionType && (
                  <MDBox>
                    <MDTypography
                      variant="caption"
                      color="secondary"
                      fontWeight="bold"
                      display="block"
                    >
                      Region
                    </MDTypography>
                      <MDTypography variant="caption">{campaign?.regionType}</MDTypography>
                  </MDBox>
                )}
                {campaign.startDate && campaign.endDate && (
                  <MDBox>
                    <MDTypography
                      variant="caption"
                      color="secondary"
                      fontWeight="bold"
                      display="block"
                    >
                      Period
                    </MDTypography>
                    <MDTypography variant="caption">
                      {campaign.startDate} → {campaign.endDate}
                    </MDTypography>
                  </MDBox>
                )}
              </MDBox>
            </Collapse>
          </MDBox>
        )}
      </MDBox>

      <MDBox display="flex" alignItems="center" gap={1}>
        <MDButton
          onClick={() => navigate(`/edit-advertisement/${data?.id}`)}
          variant="text"
          color={darkMode ? "white" : "dark"}
          size="small"
        >
          <Icon>edit</Icon>&nbsp;Edit
        </MDButton>
        <MDButton
          onClick={() => setModal(data)}
          variant="text"
          color={data?.active ? "error" : "success"}
          size="small"
        >
          <Icon>{data?.active ? "highlight_off" : "check_circle"}</Icon>
          &nbsp;{data?.active ? "Deactivate" : "Activate"}
        </MDButton>
        <MDButton
          onClick={handleMenuOpen}
          variant="text"
          color={darkMode ? "white" : "dark"}
          size="medium"
          iconOnly
        >
          <Icon>more_vert</Icon>
        </MDButton>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <MenuItem onClick={handleDelete} sx={{ color: "error.main" }}>
            <Icon sx={{ mr: 1 }}>delete</Icon>
            Delete
          </MenuItem>
        </Menu>
      </MDBox>
    </MDBox>
  );
}

AdListItem.defaultProps = {
  noGutter: false,
};

AdListItem.propTypes = {
  data: PropTypes.object.isRequired,
  noGutter: PropTypes.bool,
  setModal: PropTypes.func.isRequired,
  onView: PropTypes.func,
};

export default AdListItem;
